<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { formatDate } from '@/utils'
import { examModule } from '@/store/exam/module'
import { organizationModule } from '@/store/organization/module'
import { licenseModule } from '@/store/license/module'
import { instructorModule } from '@/store/instructor/module'
import { studentModule } from '@/store/student/module'
import { groupModule } from '@/store/group/module'
import BrandColors from '@pocketprep/ui-kit/pocketprep-export.module.scss'
import { userModule } from '@/store/user/module'
import UIKit, {
    type ITableColumn,
    type ITableSortSettings,
    type TTableRow,
} from '@pocketprep/ui-kit'
import type { Study } from '@pocketprep/types'
type TStudent = {
    id: string
    student: string
    examDate: string
    sortName: string
    styles: { [key: string]: string }
    isPending: boolean
}

type TStudentStatusOptions = {
    id: string
    label: string
    enabled: boolean
}

const {
    SidePanel,
    Button: PocketButton,
    Radio: PocketRadio,
    Select: PocketSelect,
    Input: PocketInput,
    Icon,
    Table,
    Checkbox,
    OverflowTooltip,
    Search,
    TablePagination,
    FilterDropdown,
    Tag,
    Pill,
    EmptyState,
    Errors,
} = UIKit

const emit = defineEmits<{
    close: [value?: boolean]
}>()

const groupName = ref('')
const examPrep = ref<null | { value: string; label: string }>(null)
const disableExamSelection = ref(false)
const usePermission = ref<null | { value: 1 | 2; label: string }>(null)
const editPermission = ref<null | { value: 1 | 2; label: string }>(null)
const activeTabName = ref('Group Details')
const studentIds = ref<string[]>([])
const isLoading = ref(false)
const tabs = [
    'Group Details',
    'Add Students',
    'Preview Group',
]
const searchString = ref('')
const perPage = ref(10)
const currentPage = ref(0)
const searchDebounceTimer = ref<ReturnType<typeof setTimeout> | null>(null)
const studentColumns = ref<ITableColumn[]>([{
    name: 'Student',
    propName: 'student',
    styles: {
        width: '240px',
    },
}, {
    name: 'Exam Date',
    propName: 'examDate',
    styles: {
        width: '95px',
    },
}, {
    propName: 'status',
    isLabelHidden: true,
    styles: {
        width: '58px',
    },
}])
const selectedStudentColumns = ref<ITableColumn[]>([{
    name: 'Student',
    propName: 'student',
    styles: {
        width: '230px',
    },
}, {
    name: 'Exam Date',
    propName: 'examDate',
    styles: {
        width: '120px',
    },
}, {
    name: 'Status',
    propName: 'status',
    styles: {
        width: '58px',
    },
}, {
    propName: 'remove',
    isLabelHidden: true,
    styles: {
        width: '72px',
    },
}])
const sortSettings = ref<ITableSortSettings>({ column: studentColumns.value[0], direction: 1 })
const rowHoverId = ref<string | number | null>(null)

const checkboxOptions = ref<TStudentStatusOptions[]>([
    {
        id: 'active',
        label: 'Active',
        enabled: false,
    }, {
        id: 'pending',
        label: 'Pending',
        enabled: false,
    },
])
const studentPreviewFilterOptions = ref<TStudentStatusOptions[]>([
    {
        id: 'active',
        label: 'Active',
        enabled: false,
    }, {
        id: 'pending',
        label: 'Pending',
        enabled: false,
    },
])
const allStudentsSelected = ref(false)
const goToAddStudentsTab = () => {
    activeTabName.value = 'Add Students'
}

const orgExamIdGuidLib = computed(() => {
    return examModule.getters.getOrgExamIdGuidLib()
})

const yourOrgExamIds = computed(() => {
    return examModule.getters.getYourOrgExamIds()
})

const currentOrgGroups = computed(() => {
    return groupModule.getters.getCurrentOrgGroups()
})

const currentOrgStudents = computed(() => {
    return studentModule.getters.getCurrentOrgStudents()
})

const currentOrgStudentUsers = computed(() => {
    return studentModule.getters.getCurrentOrgStudentUsers()
})

const currentOrgStudentUEM = computed(() => {
    return studentModule.getters.getCurrentOrgStudentUEM()
})

const currentOrgInstructor = computed(() => {
    return instructorModule.getters.getCurrentOrgInstructor()
})

const currentOrgLicenses = computed(() => {
    return licenseModule.getters.getCurrentOrgLicenses()
})

const currentOrganization = computed(() => {
    return organizationModule.getters.getCurrentOrganization()
})

const isAddStudentsBtnDisabled = computed(() => {
    return !groupName.value
        || !examPrep.value
        || !usePermission.value
        || !editPermission.value
        || isDuplicateGroup.value
})

const isDuplicateGroup = computed(() => {
    const selectedExamPrep = examPrep.value
    if (!selectedExamPrep || !groupName.value) {
        return false
    }
    const examGuid = orgExamIdGuidLib.value[selectedExamPrep.value]
    const newGroupCompositeKey = `${examGuid}/${currentOrgInstructor.value?.objectId}/${groupName.value}`
    return newGroupCompositeKey in groupsByCompositeKey.value
        && groupsByCompositeKey.value[newGroupCompositeKey]?.length
})

const emptyStateTitle = computed(() => {
    return searchString.value
        ? `No results for "${searchString.value}"`
        : 'No results'
})

const emptyStateMessage = computed(() => {
    return 'There are no results that match your filter and search criteria.'
})

const oneOrgExamPerExamGuid = computed(() => {
    return examModule.getters.getOneOrgExamPerExamGuid()
})

const mappedExams = computed(() => {
    if (!oneOrgExamPerExamGuid.value) {
        return []
    }
    return oneOrgExamPerExamGuid.value.reduce((acc, exam) => {
        if (!exam || (!currentOrgInstructor.value?.isAdmin && !yourOrgExamIds.value.includes(exam.objectId))) {
            return acc
        }
        acc.push({
            label: exam.displayName,
            value: exam.objectId,
        })
        return acc
    }, [] as { label: string; value: string }[])
})

const groupsByCompositeKey = computed(() => {
    return groupModule.getters.getGroupsByGroupCompositeKey()
})

const studentLicenseLib = computed(() => {
    const orgExamId = examPrep.value?.value
    if (!orgExamId) {
        return {}
    }
    const examGuid = orgExamIdGuidLib.value[orgExamId]
    return currentOrgLicenses.value.reduce((acc, license) => {
        const orgStudentId = license.orgStudent?.objectId
        if (!orgStudentId || license.examGuid !== examGuid) {
            return acc
        }
        acc[orgStudentId] = license
        return acc
    }, {} as { [key: string]: Study.Class.LicenseJSON })
})

const mappedStudents = computed(() => {
    const examPrepVal = examPrep.value
    const instructor = currentOrgInstructor.value
    if (!instructor || !examPrepVal) {
        return []
    }
    const group = Object.values(currentOrgGroups.value).find(g =>
        g.orgExam.objectId === examPrepVal.value
        && g.type === 'system'
        && (
            (!g.owner && instructor.isAdmin)
            || (g.owner?.objectId === instructor.objectId && !instructor.isAdmin)
        )
    )

    return (group?.orgStudents || [])
        .reduce((acc, studentPointer) => {
            const student = currentOrgStudents.value[studentPointer.objectId]
            const studentUser = student?.user && currentOrgStudentUsers.value[student.user.objectId]
            const studentLicense = studentLicenseLib.value[studentPointer.objectId]

            if (!studentLicense) {
                return acc
            }

            const styles: { [key: string]: string } = {
                cursor: activeTabName.value === 'Preview Group' ? 'default' : 'pointer',
                userSelect: 'none',
            }
            if (rowHoverId.value === student?.objectId) {
                styles.backgroundColor = `${BrandColors.grayBackground}60`
            }

            if (studentUser) {
                const studentName = (student.displayFirstName || student.displayLastName)
                    ? [ student.displayFirstName, student.displayLastName ].join(' ')
                    : [ studentUser.firstName, studentUser.lastName ].join(' ')
                const sortName = (student.displayFirstName || student.displayLastName)
                    ? [ student.displayLastName, student.displayFirstName ].join(' ')
                    : [ studentUser.lastName, studentUser.firstName ].join(' ')
                const examDate = '--'
                acc.push({
                    id: student.objectId,
                    student: studentName,
                    examDate,
                    sortName,
                    styles,
                    isPending: false,
                })
            } else {
                const id = studentLicense.orgStudent?.objectId
                const studentEmail = studentLicense.userEmail
                if (!id || !studentEmail) {
                    return acc
                }
                styles.color = BrandColors.ash || ''
                acc.push({
                    id: student?.objectId || '',
                    student: studentEmail,
                    examDate: '--',
                    sortName: studentEmail,
                    styles,
                    isPending: true,
                })
            }
            return acc
        }, [] as TStudent[])
})

const enabledFilterStatuses = computed(() => {
    return checkboxOptions.value.reduce<string[]>((acc, s) => {
        if (s.enabled) {
            const first = s.label.split(' ')[0]
            if (first !== undefined) {
                acc.push(first)
            }
        }
        return acc
    }, [])
})

const enabledFilterStatusesInPreview = computed(() => {
    return studentPreviewFilterOptions.value.reduce<string[]>((acc, s) => {
        if (s.enabled) {
            const first = s.label.split(' ')[0]
            if (first !== undefined) {
                acc.push(first)
            }
        }
        return acc
    }, [])
})

const mappedSelectedStudents = computed(() => {
    return mappedStudents.value.filter(s => studentIds.value.includes(s.id)
        && (!enabledFilterStatusesInPreview.value.length
            || enabledFilterStatusesInPreview.value.includes(s.isPending ? 'Pending' : 'Active')))
})

const pagedMappedSelectedStudents = computed(() => {
    const perPageVal = perPage.value
    return mappedSelectedStudents.value
        .slice(currentPage.value * perPageVal, (currentPage.value * perPageVal) + perPageVal)
})

const activeStudents = computed(() => {
    return activeTabName.value === 'Add Students' ? mappedStudents.value : activeTabStudents.value
})

const activeCountForGroup = computed(() => {
    return activeStudents.value.filter(ms => ms && !ms.isPending).length
})

const pendingCountForGroup = computed(() => {
    return activeStudents.value.filter(ms => ms?.isPending).length
})

const enabledFilters = computed(() => {
    return enabledFilterStatuses.value.map(efs => ({
        id: efs.toLocaleLowerCase(),
        label: efs,
        showClose: true,
    }))
})

const enabledPreviewFilters = computed(() => {
    return enabledFilterStatusesInPreview.value.map(efs => ({
        id: efs.toLocaleLowerCase(),
        label: efs,
        showClose: true,
    }))
})

const setFilterOptions = () => {
    checkboxOptions.value = [
        {
            id: 'active',
            label: `Active (${activeCountForGroup.value})`,
            enabled: isFilterEnabled('active'),
        },
        {
            id: 'pending',
            label: `Pending (${pendingCountForGroup.value})`,
            enabled: isFilterEnabled('pending'),
        },
    ]
    currentPage.value = 0
}

const fetchUEMForPagedStudents = async () => {
    await nextTick()
    if (!pagedActiveTabStudents.value.length || !examPrep.value) {
        return
    }
    const studentsToFetch = pagedActiveTabStudents.value.map(pagedStudent => pagedStudent.id)
    if (studentsToFetch.length) {
        await studentModule.actions.fetchOrgStudentUEMForStudents(studentsToFetch)
    }
}

const studentsSelected = computed(() => {
    return (studentIds.value.length && activeTabStudents.value.length
        && studentIds.value.length === activeTabStudents.value.length)
        ? 'all'
        : studentIds.value.length === 0 && !allStudentsSelected.value ? 'none' : 'intermediate'
})

const mappedModelValue = computed(() => {
    return studentsSelected.value === 'all'
        ? true
        : studentsSelected.value === 'none' ? false : null
})

const isFilterEnabled = (status: string) => {
    return checkboxOptions.value.some(s => s.id === status && s.enabled === true)
}

const removeFilter = (options: { filterId: string; table: 'add' | 'preview' }) => {
    if (options.table === 'add') {
        checkboxOptions.value = checkboxOptions.value
            .map(o => o.id === options.filterId ? { ...o, enabled: false } : o)
    } else {
        studentPreviewFilterOptions.value = studentPreviewFilterOptions.value
            .map(o => o.id === options.filterId ? { ...o, enabled: false } : o)
    }
}

const selectAllStudents = () => {
    if (studentsSelected.value === 'all' || studentsSelected.value === 'intermediate') {
        studentIds.value = []
    } else {
        studentIds.value = [ ...activeTabStudents.value.map(ats => ats.id) ]
    }
}

const filteredMappedStudents = computed(() => {
    const sortSettingsVal = sortSettings.value
    return mappedStudents.value.filter(s => {
        const searchStringVal = searchString.value
        const studentStatus = s.isPending ? 'Pending' : 'Active'
        const matchesSearch = !searchStringVal || s.student.toLowerCase().includes(searchStringVal.toLowerCase())
        const matchesFilter = !enabledFilterStatuses.value.length
            || enabledFilterStatuses.value.includes(studentStatus)
        return matchesSearch && matchesFilter
    })
        .sort((a, b) => {
            if (sortSettingsVal?.column) {
                if (sortSettingsVal.column.name === 'Student Name') {
                    return sortSettingsVal.direction * a.sortName.localeCompare(b.sortName)
                }
                if (sortSettingsVal.column.name === 'Exam Date') {
                    const aExamDate = new Date(a.examDate).getTime() || 0
                    const bExamDate = new Date(b.examDate).getTime() || 0
                    return sortSettingsVal.direction * (bExamDate - aExamDate)
                }
            }
            return -1
        })
})

const activeTabStudents = computed(() => {
    return (activeTabName.value === 'Add Students' ? filteredMappedStudents.value : mappedSelectedStudents.value)
        .filter(s => s.student.includes(searchString.value))
})

const pagedActiveTabStudents = computed(() => {
    const perPageVal = perPage.value
    const pagedStudents = activeTabStudents.value
        .slice(currentPage.value * perPageVal, (currentPage.value * perPageVal) + perPageVal)

    if (!examPrep.value) {
        return pagedStudents
    }

    const examGuid = orgExamIdGuidLib.value[examPrep.value.value]

    return pagedStudents.map(pagedStudent => {
        const student = currentOrgStudents.value[pagedStudent.id]
        const studentUser = student?.user && currentOrgStudentUsers.value[student.user.objectId]

        if (!studentUser) {
            return pagedStudent
        }

        const studentUEM = currentOrgStudentUEM.value[studentUser.objectId]?.find(
            uem => uem.examGuid === examGuid
        )

        return {
            ...pagedStudent,
            examDate: studentUEM && studentUEM.scheduledExamDate
                ? formatDate(studentUEM.scheduledExamDate.iso)
                : pagedStudent.examDate,
        }
    })
})

const emitClose = () => {
    emit('close', true)
}

watch(studentIds, setFilterOptions)
watch(currentPage, fetchUEMForPagedStudents)
watch(searchString, () => {
    if (searchDebounceTimer.value) {
        clearTimeout(searchDebounceTimer.value)
    }
    searchDebounceTimer.value = setTimeout(() => {
        fetchUEMForPagedStudents()
        searchDebounceTimer.value = null
    }, 1000)
})
watch(() => pagedActiveTabStudents.value.length, async (newLength) => {
    if (newLength > 0 && examPrep.value) {
        await fetchUEMForPagedStudents()
    }
})
watch(usePermission, (newVal) => {
    if (newVal?.value === 2) {
        editPermission.value = { value: 2, label: '' }
    }
}, { deep: true })

onMounted(async () => {
    await Promise.all([
        examModule.actions.fetchCurrentOrgExams(),
        groupModule.actions.fetchCurrentOrgGroups(),
        studentModule.actions.fetchCurrentOrgStudents(),
        studentModule.actions.fetchCurrentOrgStudentUsers(),
        instructorModule.actions.fetchCurrentOrgInstructor(),
        licenseModule.actions.fetchCurrentOrgLicenses(),
        examModule.actions.fetchYourOrgExamIds(),
    ])

    if (mappedExams.value.length <= 1 && mappedExams.value[0]) {
        examPrep.value = mappedExams.value[0]
        disableExamSelection.value = true
    }

    setFilterOptions()
    await fetchUEMForPagedStudents()

    await userModule.actions.trackEvent('New_Group_Started')
})

onBeforeUnmount(() => {
    if (searchDebounceTimer.value) {
        clearTimeout(searchDebounceTimer.value)
        searchDebounceTimer.value = null
    }
})

const nextTab = () => {
    const currentIndex = tabs.indexOf(activeTabName.value)
    if (currentIndex === tabs.length - 1) {
        return
    }
    searchString.value = ''
    currentPage.value = 0
    activeTabName.value = tabs[currentIndex + 1] as string
    setFilterOptions()
}

const previousTab = () => {
    const currentIndex = tabs.indexOf(activeTabName.value)
    if (currentIndex === 0) {
        return
    }
    activeTabName.value = tabs[currentIndex - 1] as string
}

const changeActiveTab = (tabName: string) => {
    if (
        (tabName === 'Preview Group' || tabName === 'Add Students')
        && !(groupName.value && examPrep.value && usePermission.value)
    ) {
        return
    }
    activeTabName.value = tabName
}

const changePage = (newPage: number) => {
    currentPage.value = newPage
}

const updateSortSettings = (newSortSettings: ITableSortSettings) => {
    sortSettings.value = newSortSettings
}

const submitAddGroup = async () => {
    if (!currentOrgInstructor.value) {
        throw new Error('submitAddGroup: Unable to find current org instructor')
    }
    if (!currentOrganization.value) {
        throw new Error('submitAddGroup: Unable to find current organization')
    }
    if (!examPrep.value) {
        throw new Error('submitAddGroup: No exam prep has been selected')
    }

    isLoading.value = true
    await groupModule.actions.addGroup({
        ownerId: currentOrgInstructor.value.objectId,
        organizationId: currentOrganization.value.objectId,
        orgStudentIds: studentIds.value,
        orgExamId: examPrep.value.value,
        name: groupName.value,
        type: usePermission.value?.value === 1 ? 'public' : 'private',
        isPubliclyEditable: editPermission.value?.value === 1 ? true : false,
    })
    organizationModule.actions.validateOrgState()
    isLoading.value = false

    emitClose()
}

const rowClicked = (row: { id: string }) => {
    if (studentIds.value.includes(row.id)) {
        studentIds.value = studentIds.value.filter(id => id !== row.id)
    } else {
        studentIds.value.push(row.id)
    }
    return studentIds.value
}

const rowEnter = (row: TTableRow) => {
    rowHoverId.value = row.id
}

const rowLeave = (row: TTableRow) => {
    if (row.id === rowHoverId.value) {
        rowHoverId.value = null
    }
}

watch(() => enabledFilters.value.length, () => {
    currentPage.value = 0
})

watch(() => enabledPreviewFilters.value.length, () => {
    currentPage.value = 0
})

watch(studentsSelected, () => {
    if (studentIds.value.length > activeTabStudents.value.length) {
        studentIds.value = activeTabStudents.value.map(ats => ats.id)
    }
})
</script>

<template>
    <Portal to="sidepanel">
        <SidePanel
            :tabs="tabs"
            :active-tab-name="activeTabName"
            :stepped-tabs="true"
            :isTeachSidePanel="true"
            name="Create New Group"
            class="add-group"
            @close="emitClose"
            @selectTab="changeActiveTab"
        >
            <template v-if="activeTabName === 'Group Details'">
                <label>Group Details</label>
                <p>Name your new group &amp; edit permissions, then add students.</p>
                <Errors
                    class="add-group__errors"
                    v-if="isDuplicateGroup"
                    :errors="['Group name already exists. Please try a different name.']"
                />
                <PocketInput
                    v-model="groupName"
                    label="Group Name"
                    class="add-group__field"
                    :error="!!isDuplicateGroup"
                />
                <PocketSelect
                    v-model="examPrep"
                    label="Exam Prep"
                    placeholder="Select exam prep"
                    class="add-group__field"
                    :data="mappedExams"
                    :disabled="disableExamSelection"
                />
                <PocketRadio
                    v-model="usePermission"
                    label="Who can <strong>use</strong> it?"
                    class="add-group__field add-group__radio-field add-group__use-permission"
                    :data="[
                        { label: 'Colleagues in organization', value: 1 },
                        { label: 'Just me', value: 2 }
                    ]"
                />
                <PocketRadio
                    v-model="editPermission"
                    label="Who can <strong>edit</strong> this group?"
                    class="add-group__field add-group__radio-field add-group__edit-permission"
                    :disabled="!usePermission || usePermission.value !== 1"
                    :data="[
                        { label: 'Colleagues in organization', value: 1 },
                        { label: 'Just me', value: 2 }
                    ]"
                />
            </template>
            <template v-else-if="activeTabName === 'Add Students'">
                <label>Add Students</label>
                <p>Select {{ examPrep && examPrep.label }} students to add to your group.</p>
                <div class="add-group__table-head">
                    <Search
                        v-model="searchString"
                        class="add-group__table-search"
                        type="white"
                        placeholder="Search"
                        @input="setFilterOptions"
                    />
                    <FilterDropdown
                        v-model="checkboxOptions"
                        class="add-group__filter"
                        filter-options-label="FILTER BY STATUS"
                        filterLabel=""
                        :radioEntries="[]"
                        :checkboxEntries="checkboxOptions"
                    />
                    <TablePagination
                        :per-page="perPage"
                        unit="students"
                        :current-page="currentPage"
                        :total="filteredMappedStudents.length"
                        @changePage="changePage($event)"
                    />
                </div>
                <div
                    v-if="enabledFilters.length"
                    class="add-group__filters">
                    <span class="add-group__filters-label">Filters:</span>
                    <Pill
                        v-for="filter in enabledFilters"
                        :key="filter.id"
                        :show-close="true"
                        class="students-table__filter-chip"
                        @click="removeFilter({filterId: filter.id, table: 'add'})"
                    >
                        {{ filter.label }}
                    </Pill>
                </div>
                <div class="add-group__bulk-actions">
                    <div
                        class="add-group__bulk-actions-checkbox-container"
                    >
                        <Checkbox
                            :modelValue="mappedModelValue"
                            class="add-group__bulk-actions-checkbox"
                            @click="selectAllStudents"
                        />
                    </div>
                    <div
                        class="add-group__bulk-actions-num-checked"
                    >
                        {{ studentIds.length }} of {{ filteredMappedStudents.length }} selected
                    </div>
                </div>
                <Table
                    key="add-group__students"
                    :rows="pagedActiveTabStudents"
                    :columns="studentColumns"
                    :default-sort="sortSettings"
                    :sort-toggle-order="[ 1, -1 ]"
                    :show-header="false"
                    class="add-group__students-table"
                    @sort="updateSortSettings"
                    @rowEnter="rowEnter"
                    @rowLeave="rowLeave"
                    @rowClicked="rowClicked({id: $event.id as string})"
                >
                    <template #tableCellValue="{ row, column }">
                        <div
                            v-if="column.propName === 'student'"
                            class="add-group__checkbox-name"
                        >
                            <Checkbox :modelValue="studentIds.includes(row.id)" class="add-group__checkbox" />
                            <div class="add-group__name">
                                <OverflowTooltip>
                                    {{ row.student }}
                                </OverflowTooltip>
                            </div>
                        </div>
                        <template v-if="column.propName === 'status'">
                            <Tag
                                v-if="row.isPending"
                                :isPendingTag="true"
                                size="small"
                            >
                                <template #tagMessage>
                                    Pending
                                </template>
                            </Tag>
                            <Tag
                                v-else
                                :isActiveTag="true"
                                size="small"
                            >
                                <template #tagMessage>
                                    Active
                                </template>
                            </Tag>
                        </template>
                    </template>
                    <template #tableRows>
                        <EmptyState
                            v-if="!filteredMappedStudents.length"
                            class="add-group__empty-state"
                            :title="emptyStateTitle"
                            :message="emptyStateMessage"
                        >
                            <template #imageRow>
                                <div />
                            </template>
                        </EmptyState>
                    </template>
                </Table>
            </template>
            <template v-else-if="activeTabName === 'Preview Group'">
                <label>Preview Group</label>
                <p>
                    Your group has {{ studentIds.length }} {{ examPrep && examPrep.label }}
                    student{{ studentIds.length === 1 ? '' : 's' }}. Look right?
                </p>
                <div class="add-group__table-head">
                    <Search
                        v-model="searchString"
                        class="add-group__table-search"
                        type="white"
                        placeholder="Search"
                        @input="setFilterOptions"
                    />
                    <FilterDropdown
                        v-model="studentPreviewFilterOptions"
                        class="add-group__filter"
                        filter-options-label="FILTER BY STATUS"
                        filterLabel=""
                        :radioEntries="[]"
                        :checkboxEntries="studentPreviewFilterOptions"
                    />
                    <TablePagination
                        :per-page="perPage"
                        unit="students"
                        :current-page="currentPage"
                        :total="mappedSelectedStudents.length"
                        @changePage="changePage($event)"
                    />
                </div>
                <div
                    v-if="enabledPreviewFilters.length"
                    class="add-group__filters">
                    <span class="add-group__filters-label">Filters:</span>
                    <Pill
                        v-for="filter in enabledPreviewFilters"
                        :key="filter.id"
                        :show-close="true"
                        class="students-table__filter-chip"
                        @click="removeFilter({filterId: filter.id, table: 'preview'})"
                    >
                        {{ filter.label }}
                    </Pill>
                </div>
                <Table
                    key="add-group__selected-students"
                    :rows="pagedMappedSelectedStudents"
                    :columns="selectedStudentColumns"
                    :default-sort="sortSettings"
                    :sort-toggle-order="[ 1, -1 ]"
                    :show-header="false"
                    :table-cell-styles="{
                        maxWidth: '210px',
                    }"
                    :table-column-label-styles="{
                        maxWidth: '210px',
                    }"
                    class="add-group__students-table"
                    @sort="updateSortSettings"
                >
                    <template #tableCellValue="{ row, column }">
                        <template v-if="column.propName === 'status'">
                            <Tag
                                v-if="row.isPending"
                                :isPendingTag="true"
                                size="small"
                            >
                                <template #tagMessage>
                                    Pending
                                </template>
                            </Tag>
                            <Tag
                                v-else
                                :isActiveTag="true"
                                size="small"
                            >
                                <template #tagMessage>
                                    Active
                                </template>
                            </Tag>
                        </template>
                        <PocketButton
                            v-if="column.propName === 'remove'"
                            class="add-group__remove"
                            type="tertiary"
                            @click="rowClicked(row)"
                        >
                            Remove
                        </PocketButton>
                    </template>
                </Table>
                <PocketButton
                    type="secondary"
                    class="add-group__add-student"
                    @click="goToAddStudentsTab"
                >
                    + Add Student
                </PocketButton>
            </template>
            <template #action>
                <template v-if="activeTabName === 'Group Details'">
                    <PocketButton
                        type="primary"
                        :disabled="!!isAddStudentsBtnDisabled"
                        @click="nextTab"
                    >
                        Add Students <Icon type="arrow" class="add-group__add-students-arrow" />
                    </PocketButton>
                </template>
                <template v-else-if="activeTabName === 'Add Students'">
                    <PocketButton
                        type="primary"
                        @click="nextTab"
                    >
                        Preview Group <Icon type="arrow" class="add-group__preview-group-arrow" />
                    </PocketButton>
                </template>
                <template v-else-if="activeTabName === 'Preview Group'">
                    <PocketButton
                        type="primary"
                        :disabled="isLoading"
                        @click="submitAddGroup"
                    >
                        Add Group
                    </PocketButton>
                </template>
            </template>

            <template #details>
                <PocketButton
                    v-if="activeTabName !== 'Group Details'"
                    type="tertiary"
                    class="add-group__back"
                    @click="previousTab"
                >
                    <Icon type="arrow" class="add-group__back-arrow" />Back
                </PocketButton>
            </template>
        </SidePanel>
    </Portal>
</template>

<style lang="scss" scoped>
.add-group {
    :deep(*) {
        .uikit-side-panel__tab--stepper {
            margin-bottom: 0;
        }
        .uikit-side-panel__tab--stepper::after {
            bottom: 0;
        }
    }

    &__errors {
        margin: -10px 0 25px;
    }

    &__field {
        margin-bottom: 21px;
        max-width: 363px;
    }

    &__radio-field {
        margin-left: 0;
    }

    &__back-arrow {
        margin-right: 10px;
        transform: rotate(180deg);
    }

    &__add-student {
        padding: 12px 11px;
        font-weight: 600;
        margin-left: -11px;
    }

    &__add-students-arrow,
    &__preview-group-arrow {
        margin-left: 6px;
    }

    &__students-table {
        margin-left: -11px;
    }

    &__checkbox-name {
        display: flex;
        align-items: center;
    }

    &__checkbox {
        margin-right: 10px;
    }

    &__name {
        width: calc(100% - 28px);
    }

    &__table-head {
        display: flex;
        margin: 11px 0 20px;
        justify-content: space-between;
        align-items: center;
    }

    &__table-search {
        width: 262px;
    }

    &__bulk-actions {
        display: flex;
        margin-bottom: 11px;
    }

    &__bulk-actions-num-checked {
        font-size: 12px;
        line-height: 16px;
        margin: 2px 30px;
        margin-left: 20px;
    }

    &__filters {
        height: 30px;
        margin: 10px 0 8px 3px;
    }

    &__filters-label {
        font-size: 13px;
        line-height: 22px;
        color: $slate-03;
        margin-right: 9px;
        display: inline-block;
        vertical-align: top;
    }

    &__empty-state {
        margin: 0 auto;
        padding-top: 40px;
    }
}
</style>
